import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Loader2, Inbox, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchSubmissions, downloadCsv, deleteSubmission, clearToken, getToken } from "../lib/adminApi";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const data = await fetchSubmissions();
        setRows(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearToken();
          navigate("/admin/login");
        } else {
          toast.error("Failed to load submissions.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadCsv();
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteSubmission(pendingDelete.id);
      setRows((prev) => prev.filter((r) => r.id !== pendingDelete.id));
      toast.success("Lead deleted.");
      setPendingDelete(null);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      } else {
        toast.error("Failed to delete. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EDEFF7]">
      <header className="bg-white border-b border-[#D3D6E0] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <img src="/logo-communitytax.webp" alt="Community Tax" className="h-6 w-auto" />
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#40424D] hover:text-[#0262B4] transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#1E1E24]">Partner Applications</h1>
            <p className="text-[#40424D] text-base mt-1" data-testid="admin-count">
              {loading ? "Loading…" : `${rows.length} total submission${rows.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <button
            onClick={onExport}
            disabled={exporting || rows.length === 0}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full ct-grad-navy-btn text-white font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
            data-testid="admin-export"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#40424D]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-[24px] ct-shadow-card p-16 text-center">
            <Inbox className="w-10 h-10 text-[#9DA2B3] mx-auto mb-4" />
            <p className="text-[#40424D] text-base">No submissions yet.</p>
          </div>
        ) : (
          <div className="bg-white rounded-[24px] ct-shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-table">
                <thead>
                  <tr className="bg-[#F1F5F9] text-[#1E1E24] text-sm">
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Submitted</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Name</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Company</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Work Email</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Partner Type</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Role</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Company Size</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Objective</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap">Models</th>
                    <th className="px-5 py-4 font-bold whitespace-nowrap text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[#EDEFF7] text-[#40424D] text-sm hover:bg-[#F7F9FC]">
                      <td className="px-5 py-4 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="px-5 py-4 whitespace-nowrap font-medium text-[#1E1E24]">{r.full_name}</td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        {r.company_website ? (
                          <a href={r.company_website} target="_blank" rel="noopener noreferrer" className="text-[#0262B4] hover:underline">
                            {r.company_name}
                          </a>
                        ) : r.company_name}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <a href={`mailto:${r.work_email}`} className="text-[#0262B4] hover:underline">{r.work_email}</a>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">{r.partner_type}</td>
                      <td className="px-5 py-4 whitespace-nowrap">{r.role}</td>
                      <td className="px-5 py-4 whitespace-nowrap">{r.company_size}</td>
                      <td className="px-5 py-4">{r.primary_objective}</td>
                      <td className="px-5 py-4">{(r.partnership_models || []).join(", ")}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => setPendingDelete(r)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#9DA2B3] hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label={`Delete lead ${r.full_name}`}
                          data-testid={`delete-lead-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E1E24]/50 backdrop-blur-sm px-6"
          data-testid="delete-confirm-modal"
        >
          <div className="bg-white rounded-[24px] ct-shadow-hover p-8 max-w-md w-full">
            <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mb-5">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1E1E24] mb-2">Delete this lead?</h3>
            <p className="text-[#40424D] text-base mb-6">
              You're about to permanently delete the submission from{" "}
              <span className="font-semibold text-[#1E1E24]">{pendingDelete.full_name}</span>
              {pendingDelete.company_name ? ` (${pendingDelete.company_name})` : ""}. This can't be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#40424D] hover:bg-[#EDEFF7] transition-colors disabled:opacity-50"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
                data-testid="delete-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
